import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, Shuffle, Plus, Trash2, DollarSign, Users, Mail, History, Settings, Crown, Star } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { SEO } from '../../components/common/SEO';
import { SignInModal } from '../../components/SignInModal';
import { SignUpModal } from '../../components/SignUpModal';
import { useAuth } from '../../hooks/useAuth';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import toast from 'react-hot-toast';

interface Participant {
  id: string;
  name: string;
  email: string;
  preferences: string;
  exclusions: string;
  wishlist_url?: string;
  budget?: number;
}

interface SecretSantaGroup {
  id: string;
  name: string;
  budget: number;
  participants: Participant[];
  assignments: Record<string, string>;
  created_at: string;
  organizer_id: string;
}

interface GiftSuggestion {
  title: string;
  price: number;
  image?: string;
  url?: string;
  reason: string;
}

export const SecretSantaPage: React.FC = () => {
  const { user } = useAuth();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [newParticipant, setNewParticipant] = useState({ 
    name: '', 
    email: '', 
    preferences: '', 
    exclusions: '', 
    wishlist_url: '',
    budget: 0 
  });
  const [showResults, setShowResults] = useState(false);
  const [lookupName, setLookupName] = useState('');
  const [currentAssignment, setCurrentAssignment] = useState<string | null>(null);
  
  // Enhanced features for logged-in users
  const [groupBudget, setGroupBudget] = useState(50);
  const [groupName, setGroupName] = useState('');
  const [savedGroups, setSavedGroups] = useState<SecretSantaGroup[]>([]);
  const [giftSuggestions, setGiftSuggestions] = useState<GiftSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Modal state
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const canonicalUrl = useCanonicalUrl();

  const addParticipant = () => {
    if (!newParticipant.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    const participant: Participant = {
      id: Date.now().toString(),
      ...newParticipant,
      budget: newParticipant.budget || groupBudget
    };

    setParticipants(prev => [...prev, participant]);
    setNewParticipant({ 
      name: '', 
      email: '', 
      preferences: '', 
      exclusions: '', 
      wishlist_url: '',
      budget: 0 
    });
    toast.success('Participant added!');
  };

  const removeParticipant = (id: string) => {
    setParticipants(prev => prev.filter(p => p.id !== id));
    setShowResults(false);
  };

  const generateAssignments = () => {
    if (participants.length < 2) {
      toast.error('You need at least 2 participants');
      return;
    }

    const shuffled = [...participants].sort(() => Math.random() - 0.5);
    const newAssignments: Record<string, string> = {};

    for (let i = 0; i < shuffled.length; i++) {
      const giver = shuffled[i];
      const receiver = shuffled[(i + 1) % shuffled.length];
      newAssignments[giver.id] = receiver.name;
    }

    setAssignments(newAssignments);
    setShowResults(true);
    toast.success('Secret Santa assignments generated!');
  };

  const resetAssignments = () => {
    setAssignments({});
    setShowResults(false);
    setCurrentAssignment(null);
    setLookupName('');
  };

  const lookupAssignment = () => {
    if (!lookupName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    const participant = participants.find(p => 
      p.name.toLowerCase().trim() === lookupName.toLowerCase().trim()
    );

    if (!participant) {
      toast.error('Name not found in participants list');
      return;
    }

    if (!assignments[participant.id]) {
      toast.error('No assignment found. Please generate assignments first.');
      return;
    }

    setCurrentAssignment(assignments[participant.id]);
    
    // Generate gift suggestions for logged-in users
    if (user && participant.wishlist_url) {
      generateGiftSuggestions(participant);
    }
    
    toast.success('Assignment found!');
  };

  // Enhanced functions for logged-in users
  const saveGroup = () => {
    if (!user) {
      toast.error('Please sign in to save groups');
      return;
    }

    if (!groupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    if (participants.length < 2) {
      toast.error('You need at least 2 participants');
      return;
    }

    const group: SecretSantaGroup = {
      id: Date.now().toString(),
      name: groupName,
      budget: groupBudget,
      participants: [...participants],
      assignments: { ...assignments },
      created_at: new Date().toISOString(),
      organizer_id: user.id
    };

    setSavedGroups(prev => [...prev, group]);
    toast.success('Group saved successfully!');
  };

  const loadGroup = (group: SecretSantaGroup) => {
    setParticipants(group.participants);
    setAssignments(group.assignments);
    setGroupBudget(group.budget);
    setGroupName(group.name);
    setShowResults(Object.keys(group.assignments).length > 0);
    toast.success(`Loaded group: ${group.name}`);
  };

  const generateGiftSuggestions = (participant: Participant) => {
    // Mock gift suggestions based on preferences and budget
    const suggestions: GiftSuggestion[] = [
      {
        title: "Gift Card to Popular Store",
        price: participant.budget || groupBudget,
        reason: "Perfect budget match and always appreciated"
      },
      {
        title: "Coffee/Tea Gift Set",
        price: Math.floor((participant.budget || groupBudget) * 0.7),
        reason: "Great for coffee lovers, within budget"
      },
      {
        title: "Book Collection",
        price: Math.floor((participant.budget || groupBudget) * 0.6),
        reason: "Based on their preferences"
      }
    ];

    setGiftSuggestions(suggestions);
    setShowSuggestions(true);
  };

  const sendEmailNotifications = async () => {
    if (!user) {
      toast.error('Please sign in to send notifications');
      return;
    }

    if (Object.keys(assignments).length === 0) {
      toast.error('No assignments to send');
      return;
    }

    // Mock email sending
    toast.success('Email notifications sent to all participants!');
  };

  const scrollToBasicFeatures = () => {
    const element = document.getElementById('basic-features');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEO
        title="Secret Santa Generator - Organize Gift Exchanges Easily"
        description="Generate Secret Santa assignments, manage participants, and organize gift exchanges. Perfect for office parties, family gatherings, and friend groups."
        keywords={['secret santa generator', 'gift exchange', 'christmas party', 'office party', 'holiday party']}
        url={canonicalUrl}
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Secret Santa Generator
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Organize your Secret Santa gift exchange with ease. Add participants, 
              generate random assignments, and keep the secret until gift exchange day!
            </p>
          </div>

          {/* Enhanced Features for Logged-in Users */}
          <Card className="mb-8 bg-gradient-to-r from-primary-50 to-accent-50 border-primary-200">
            <div className="flex items-center space-x-3 mb-4">
              <Crown className="w-6 h-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-primary-800">Enhanced Features</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-primary-600" />
                <span className="text-sm font-medium">Budget Management</span>
              </div>
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-primary-600" />
                <span className="text-sm font-medium">Group Management</span>
              </div>
              <div className="flex items-center space-x-2">
                <Star className="w-5 h-5 text-primary-600" />
                <span className="text-sm font-medium">Gift Suggestions</span>
              </div>
            </div>
            {!user ? (
              <div className="p-4 bg-white rounded-lg border border-primary-200">
                <p className="text-gray-700 mb-3">
                  <strong>Sign in to unlock:</strong> Save groups, set budgets, get gift suggestions from wishlists, 
                  send email notifications, and access participant wishlists!
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    className="bg-primary-600 hover:bg-primary-700"
                    onClick={() => setIsSignInModalOpen(true)}
                  >
                    Sign In to Get Started
                  </Button>
                  <Button variant="outline" size="sm" onClick={scrollToBasicFeatures}>
                    Continue as Guest
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-white rounded-lg border border-primary-200">
                <p className="text-green-700 font-medium mb-2">
                  ✅ You're signed in! All enhanced features are available below.
                </p>
                <p className="text-sm text-gray-600">
                  You can save groups, set budgets, get gift suggestions, and send email notifications.
                </p>
              </div>
            )}
          </Card>

          {/* Group Management for Logged-in Users */}
          {user && (
            <Card className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Group Management</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Group Name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Office Party 2024"
                />
                <Input
                  label="Budget per Person ($)"
                  type="number"
                  value={groupBudget}
                  onChange={(e) => setGroupBudget(Number(e.target.value))}
                  placeholder="50"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={saveGroup} disabled={participants.length < 2}>
                  <Settings className="h-4 w-4 mr-2" />
                  Save Group
                </Button>
                {savedGroups.length > 0 && (
                  <Button variant="outline" onClick={() => setShowResults(false)}>
                    <History className="h-4 w-4 mr-2" />
                    Load Saved Group
                  </Button>
                )}
              </div>
              
              {/* Saved Groups */}
              {savedGroups.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium mb-2">Saved Groups:</h3>
                  <div className="space-y-2">
                    {savedGroups.map((group) => (
                      <div key={group.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <h4 className="font-medium">{group.name}</h4>
                          <p className="text-sm text-gray-600">
                            {group.participants.length} participants • ${group.budget} budget
                          </p>
                        </div>
                        <Button size="sm" onClick={() => loadGroup(group)}>
                          Load
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
          <div id="basic-features">
            <Card className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Add Participants</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Input
                label="Name"
                value={newParticipant.name}
                onChange={(e) => setNewParticipant(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Doe"
                required
              />
              <Input
                label="Email (Optional)"
                type="email"
                value={newParticipant.email}
                onChange={(e) => setNewParticipant(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Input
                label="Gift Preferences (Optional)"
                value={newParticipant.preferences}
                onChange={(e) => setNewParticipant(prev => ({ ...prev, preferences: e.target.value }))}
                placeholder="Books, coffee, gift cards"
              />
              <Input
                label="Exclusions (Optional)"
                value={newParticipant.exclusions}
                onChange={(e) => setNewParticipant(prev => ({ ...prev, exclusions: e.target.value }))}
                placeholder="No alcohol, no chocolate"
              />
            </div>
            {user && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Wishlist URL (Optional)"
                  value={newParticipant.wishlist_url}
                  onChange={(e) => setNewParticipant(prev => ({ ...prev, wishlist_url: e.target.value }))}
                  placeholder="https://getwishful.com/@username"
                />
                <Input
                  label="Personal Budget Override (Optional)"
                  type="number"
                  value={newParticipant.budget}
                  onChange={(e) => setNewParticipant(prev => ({ ...prev, budget: Number(e.target.value) }))}
                  placeholder={`${groupBudget} (uses group budget)`}
                />
              </div>
            )}
            <Button onClick={addParticipant}>
              <Plus className="h-4 w-4 mr-2" />
              Add Participant
            </Button>
            </Card>
          </div>

          {/* Participants List */}
          {participants.length > 0 && (
            <Card className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Participants ({participants.length})</h2>
              <div className="space-y-3">
                {participants.map((participant) => (
                  <div key={participant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold">{participant.name}</h3>
                      {participant.email && (
                        <p className="text-sm text-gray-600">{participant.email}</p>
                      )}
                      {participant.preferences && (
                        <p className="text-sm text-gray-500">
                          <strong>Prefers:</strong> {participant.preferences}
                        </p>
                      )}
                      {participant.exclusions && (
                        <p className="text-sm text-gray-500">
                          <strong>Avoids:</strong> {participant.exclusions}
                        </p>
                      )}
                      {user && participant.wishlist_url && (
                        <p className="text-sm text-primary-600">
                          <strong>Wishlist:</strong> <a href={participant.wishlist_url} target="_blank" rel="noopener noreferrer" className="underline">View Wishlist</a>
                        </p>
                      )}
                      {user && participant.budget && (
                        <p className="text-sm text-green-600">
                          <strong>Budget:</strong> ${participant.budget}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeParticipant(participant.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Generate Assignments */}
          {participants.length >= 2 && (
            <Card className="mb-8">
              <div className="text-center">
                <h2 className="text-xl font-semibold mb-4">Generate Secret Santa Assignments</h2>
                <p className="text-gray-600 mb-6">
                  Click the button below to randomly assign who gives gifts to whom. 
                  Each person will only see their own assignment to keep it secret!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button onClick={generateAssignments} size="lg">
                    <Shuffle className="h-5 w-5 mr-2" />
                    Generate Secret Assignments
                  </Button>
                  {showResults && (
                    <Button variant="outline" onClick={resetAssignments} size="lg">
                      Reset Assignments
                    </Button>
                  )}
                  {user && showResults && Object.keys(assignments).length > 0 && (
                    <Button onClick={sendEmailNotifications} size="lg" className="bg-green-600 hover:bg-green-700">
                      <Mail className="h-5 w-5 mr-2" />
                      Send Email Notifications
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Secret Assignment Lookup */}
          {showResults && (
            <Card className="mb-8">
              <h2 className="text-xl font-semibold mb-4">🔍 Look Up Your Secret Santa Assignment</h2>
              <p className="text-gray-600 mb-6">
                Enter your name below to see who you're giving a gift to. Keep it secret!
              </p>
              
              <div className="max-w-md mx-auto">
                <div className="mb-4">
                  <Input
                    label="Your Name"
                    value={lookupName}
                    onChange={(e) => setLookupName(e.target.value)}
                    placeholder="Enter your name exactly as added"
                    className="text-center"
                  />
                </div>
                <Button onClick={lookupAssignment} size="lg" className="w-full">
                  <Gift className="h-5 w-5 mr-2" />
                  Find My Assignment
                </Button>
                
                {currentAssignment && (
                  <div className="mt-6 p-6 bg-gradient-to-r from-primary-50 to-accent-50 rounded-lg border-2 border-primary-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
                      🎁 Your Secret Santa Assignment
                    </h3>
                    <p className="text-lg text-gray-700 text-center mb-4">
                      You are giving a gift to: <strong className="text-primary-600">{currentAssignment}</strong>
                    </p>
                    <div className="text-center">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setCurrentAssignment(null);
                          setLookupName('');
                        }}
                        size="sm"
                      >
                        Clear Assignment
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Gift Suggestions for Logged-in Users */}
          {user && showSuggestions && giftSuggestions.length > 0 && (
            <Card className="mb-8">
              <h2 className="text-xl font-semibold mb-4">🎁 Gift Suggestions</h2>
              <p className="text-gray-600 mb-6">
                Based on their preferences and budget, here are some gift ideas for {currentAssignment}:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {giftSuggestions.map((suggestion, index) => (
                  <div key={index} className="p-4 bg-gradient-to-br from-accent-50 to-primary-50 rounded-lg border border-accent-200">
                    <h3 className="font-semibold text-gray-900 mb-2">{suggestion.title}</h3>
                    <p className="text-lg font-bold text-primary-600 mb-2">${suggestion.price}</p>
                    <p className="text-sm text-gray-600 mb-3">{suggestion.reason}</p>
                    {suggestion.url && (
                      <Button size="sm" className="w-full">
                        View Gift
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 text-center">
                <Button variant="outline" onClick={() => setShowSuggestions(false)}>
                  Hide Suggestions
                </Button>
              </div>
            </Card>
          )}

          {/* Tips */}
          <Card className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Secret Santa Tips</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">🔒 Keeping It Secret</h3>
                <ul className="text-gray-600 space-y-1">
                  <li>• Only look up your own assignment</li>
                  <li>• Don't share who you're giving to</li>
                  <li>• Keep assignments private until exchange</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">💰 Setting a Budget</h3>
                <ul className="text-gray-600 space-y-1">
                  <li>• Agree on a spending limit ($10-50)</li>
                  <li>• Consider everyone's financial situation</li>
                  <li>• Include gift wrapping in budget</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">🎁 Gift Ideas</h3>
                <ul className="text-gray-600 space-y-1">
                  <li>• Gift cards to popular stores</li>
                  <li>• Books or movies</li>
                  <li>• Coffee or tea sets</li>
                  <li>• Office supplies or gadgets</li>
                </ul>
              </div>
            </div>
            
            {user && (
              <div className="mt-6 p-4 bg-gradient-to-r from-primary-50 to-accent-50 rounded-lg border border-primary-200">
                <h3 className="font-semibold text-primary-800 mb-2">🌟 Enhanced Tips for Logged-in Users</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div>
                    <ul className="space-y-1">
                      <li>• Use wishlist URLs to see what they want</li>
                      <li>• Set individual budgets for different participants</li>
                      <li>• Save groups for recurring events</li>
                    </ul>
                  </div>
                  <div>
                    <ul className="space-y-1">
                      <li>• Send email notifications automatically</li>
                      <li>• Get gift suggestions from participant wishlists</li>
                      <li>• Track assignment history</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* CTA */}
          <div className="text-center">
            <Card className="p-8 bg-white">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Need More Gift Planning Tools?
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Create wishlists, track prices, and organize all your holiday gifts in one place.
              </p>
              <Link to="/dashboard">
                <Button size="lg">
                  <Gift className="h-5 w-5 mr-2" />
                  Start Planning
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Sign In Modal */}
      <SignInModal 
        isOpen={isSignInModalOpen} 
        onClose={() => setIsSignInModalOpen(false)}
        onOpenSignUp={() => setIsSignUpModalOpen(true)}
      />
      
      {/* Sign Up Modal */}
      <SignUpModal 
        isOpen={isSignUpModalOpen} 
        onClose={() => setIsSignUpModalOpen(false)} 
      />
    </>
  );
};
